import { Component, Input, OnChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { CommonService } from 'src/app/shared/services/common.service';
import { ALL_PERIODS, ALL_YEARS, PerSiteChartsService } from 'src/app/shared/services/per-site-charts.service';
import { SiteDashboardService } from '../../site-dashboard/site-dashboard.service';

interface TruckUsageMetric {
	/** field on the per-site row holding the value */
	id: string;
	label: string;
	unit: string;
}

interface TruckUsageChart {
	metricId: string;
	title: string;
	columnNames: any[];
	rows: any[][];
	config: any;
	legend: { text: string, color: string }[];
	/** period and site behind each row, so the export can column them separately */
	rowMeta?: { periodLabel: string, siteName: string }[];
	printSectionId: string;
	/** ST-2614: the chart's width in % of its container - past 100 it scrolls sideways */
	widthPercent: number;
}

/**
 * ST-2448: "Truck Usage Data" - the energy delivered to trucks per site, as two charts
 * side by side (Total In-Use Ah and Total In-Use kWh) rather than one chart behind a
 * metric picker. Reads the same per-site rows as the utilization charts, so the numbers
 * agree with the Total InUse Ah / Total Truck InUse KWh figures shown elsewhere on the page.
 */
@Component({
	selector: 'app-truck-usage-data',
	templateUrl: './truck-usage-data.component.html'
})
export class TruckUsageDataComponent implements OnChanges {
	// untyped on purpose: the raw backend payload, nested deviceType -> workKey -> year ->
	// period -> rows, matching how every other chart on this page receives its data
	@Input() perSiteData: any = {};
	/** perSiteMeta[deviceType][siteId] = { siteName, installYear }; untyped for the same reason */
	@Input() perSiteMeta: any = {};
	@Input() sites: any[] = [];
	@Input() siteInfo: any = {};
	/** truck type -> series index, used to offer the truck type filter */
	@Input() truckTypes: any = {};

	readonly deviceTypeKey = 'iotah';

	/** decimals the tooltip shows, and the CSV export uses the same (ST-2595) */
	readonly valueDecimals = 0;

	readonly metrics: TruckUsageMetric[] = [
		{
			id: 'total_ahr',
			label: this.translateService.instant('customer.charts.total_inuse_ah'),
			unit: this.translateService.instant('customer.charts.unit_ah'),
		},
		{
			id: 'total_kwhr',
			label: this.translateService.instant('customer.charts.total_inuse_kwh'),
			unit: this.translateService.instant('customer.charts.unit_kwh'),
		},
	];

	monthlyQuarterlyPeriods = [
		{ id: 'quarter', durations: this.translateService.instant('g.quarterly') },
		{ id: 'months', durations: this.translateService.instant('g.monthly') }
	];
	selectedDuration = 'months';

	selectedYear = '';
	selectedPeriod = ALL_PERIODS;
	selectedSites = [];
	/** empty means All Truck Types, the same way an empty site list means All Sites */
	selectedTruckTypes: string[] = [];

	ddList = {
		years: [],
		periods: [],
		truckTypes: [],
	};

	/** truck type keys are sanitised for the dropdown, so keep a way back to the real value */
	truckTypesMapper = {};

	charts: TruckUsageChart[] = [];
	/** both charts share the same truck types, so one legend covers the section */
	chartLegend: { text: string, color: string }[] = [];
	showChart = false;

	/**
	 * Held as a field, not a getter. The print header binds to it, and a getter would
	 * rebuild the array on every change-detection pass - which means on every mouse
	 * move over a chart or a dropdown, several translate lookups at a time.
	 */
	printFilters: { label: string, value: string }[] = [];

	constructor(
		private translateService: TranslateService,
		public common: CommonService,
		private perSiteChartsService: PerSiteChartsService,
		private siteDashboardService: SiteDashboardService,
	) { }

	ngOnChanges() {
		this.prepareTruckTypesList();
		this.buildCharts();
	}

	get workKey(): string {
		return this.selectedDuration == 'quarter' ? 'quarters' : 'months';
	}

	/** All Years puts years on the x-axis, so the two period filters have nothing to say */
	get isAllYears(): boolean {
		return this.perSiteChartsService.isAllYears(this.selectedYear);
	}

	get isBucketAxis(): boolean {
		return this.perSiteChartsService.isBucketAxis(this.selectedYear, this.selectedPeriod);
	}

	get allowedSiteIds() {
		if (this.selectedSites?.length)
			return this.selectedSites;

		return (this.sites || []).map(site => site.id);
	}

	get title(): string {
		return this.translateService.instant('customer.charts.truck_usage_data');
	}

	get periodDisplay(): string {
		return this.perSiteChartsService.getPeriodDisplay(this.selectedPeriod, this.selectedYear, this.workKey);
	}

	/** rebuilt only when the filters change; the template binds to the cached field */
	buildPrintFilters(): { label: string, value: string }[] {
		const filters = [
			{
				label: 'g.year',
				value: this.common.getOptionLabel(this.ddList.years, this.selectedYear, { valueKey: 'id', labelKey: 'label' }),
			},
			{
				label: 'site.sites',
				value: this.common.getOptionLabel(
					this.sites, this.selectedSites,
					{ valueKey: 'id', labelKey: 'name', fallbackKey: 'site.all_sites' }
				),
			},
			{
				label: 'devices.truck_type',
				value: this.common.getOptionLabel(
					this.ddList.truckTypes, this.selectedTruckTypes,
					{ valueKey: 'id', labelKey: 'text', fallbackKey: 'site_performance.all_truck_types' }
				),
			},
		];

		if (this.isAllYears)
			return filters;

		filters.push({
			label: 'g.durations',
			value: this.common.getOptionLabel(
				this.monthlyQuarterlyPeriods, this.selectedDuration,
				{ valueKey: 'id', labelKey: 'durations' }
			),
		});

		filters.push({ label: 'customer.charts.period', value: this.periodDisplay });

		return filters;
	}

	prepareTruckTypesList() {
		// no "All Truck Types" entry: the filter is multi-select, and selecting nothing means all
		const options: { id: string, text: string }[] = [];

		for (const truckType of Object.keys(this.truckTypes || {})) {
			const truckTypeKey = truckType.replace(/[^\w]/gi, '_');
			this.truckTypesMapper[truckTypeKey] = truckType;
			options.push({ id: truckTypeKey, text: truckType });
		}

		this.ddList.truckTypes = options;
	}

	/** Year and period both come out of the data already loaded - no round trip needed */
	onLocalFilterChange() {
		this.buildCharts();
	}

	/** Truck type and site narrow the device set, so the aggregation has to be redone */
	onServerFilterChange() {
		const siteIds = this.allowedSiteIds;
		if (!siteIds.length)
			return;

		this.siteDashboardService.getTruckUsageData(siteIds, this.selectedDuration, {
			truckYear: '',
			truckType: this.selectedTruckTypes.map(truckTypeKey => this.truckTypesMapper[truckTypeKey]),
			truckTag: [],
			parentPage: 'customer',
		}).subscribe((res: any) => {
			if (res?.perSite)
				this.perSiteData = res.perSite;

			if (res?.perSiteMeta)
				this.perSiteMeta = res.perSiteMeta;

			this.buildCharts();
		});
	}

	buildCharts() {
		const years = this.perSiteChartsService.getYearOptions(
			this.perSiteData, this.deviceTypeKey, this.workKey,
			{ meta: this.perSiteMeta, siteIds: this.allowedSiteIds }
		);

		// A site + truck type pair with no trucks leaves only All Years in the list. Keep the
		// year the user picked, as Truck Utilization does, instead of jumping to All Years and
		// hiding the period filters; the chart then says there is no data.
		const keepYear = years.length <= 1 && this.ddList.years.length > 1;
		if (!keepYear) {
			this.ddList.years = years;

			if (!this.ddList.years.some(year => year.id == this.selectedYear))
				this.selectedYear = this.ddList.years[1]?.id || ALL_YEARS;
		}

		this.ddList.periods = this.perSiteChartsService.getPeriodOptions(
			this.perSiteData, this.deviceTypeKey, this.workKey, this.selectedYear
		);

		// All Months is the default the story asks for, and the fallback whenever the
		// selected month is not in the new year's list
		if (!this.ddList.periods.some(period => period.id == this.selectedPeriod))
			this.selectedPeriod = ALL_PERIODS;

		const periodLabel = this.periodDisplay;
		const bucketAxis = this.isBucketAxis;

		const buckets = bucketAxis
			? this.perSiteChartsService.getBuckets(
				this.perSiteData, this.deviceTypeKey, this.workKey, this.selectedYear, this.selectedPeriod
			)
			: [];

		const rows = bucketAxis
			? []
			: this.perSiteChartsService.getRows(
				this.perSiteData, this.deviceTypeKey, this.workKey, this.selectedYear, this.selectedPeriod
			);

		this.charts = this.metrics.map(metric => {
			const shared = {
				metricField: metric.id,
				siteIds: this.allowedSiteIds,
				periodLabel,
				unit: metric.unit,
				decimals: this.valueDecimals,
			};

			// Every view keeps the truck-type segments; only the x-axis changes. Energy adds
			// up across periods, so a year is the sum of its months.
			const built = bucketAxis
				? this.perSiteChartsService.buildStackedBySiteAcrossBuckets(buckets, {
					...shared,
					seriesIndexes: this.truckTypes,
					aggregate: 'sum',
				})
				: this.perSiteChartsService.buildStackedBySiteAndTruckType(rows, { ...shared, seriesIndexes: this.truckTypes });

			const widthPercent = this.perSiteChartsService.getChartWidthPercent(built);

			return {
				metricId: metric.id,
				title: metric.label,
				columnNames: built.columnNames,
				rows: built.rows,
				legend: built.legend,
				widthPercent,
				rowMeta: built.rowMeta,
				printSectionId: `truck-usage-data-${metric.id}`,
				config: {
					legend: 'none',
					// wider bars in the period views: the group should read as one block per month
					bar: { groupWidth: bucketAxis ? '92%' : 30 },
					isStacked: true,
					tooltip: { isHtml: true },
					vAxis: { title: metric.unit },
					...this.perSiteChartsService.getChartWidthOptions(widthPercent),
					// ST-2614: the period views put their bars on a number axis with one label per period
					...built.hAxis ? { hAxis: built.hAxis } : {},
				},
			};
		});

		this.showChart = this.charts.some(chart => chart.rows.length > 0);
		this.chartLegend = this.charts.find(chart => chart.legend.length)?.legend || [];
		this.printFilters = this.buildPrintFilters();
	}

	exportChart(chart: TruckUsageChart) {
		const alphabits = this.common.alphabetCharacters;
		const headersInfo = this.common.getCsvFileHeadersInfo({ customerName: this.siteInfo.name });

		const table = this.perSiteChartsService.buildExportTable(chart, {
			periodLabel: this.periodDisplay,
			bucketAxis: this.isBucketAxis,
			// the single-period view is stacked by truck type, so it carries a Total column
			breakdown: true,
			decimals: this.valueDecimals,
		});

		const headerObj = {};
		table.headers.forEach((column, colIdx) => headerObj[alphabits[colIdx]] = column);

		const dataObj: any[] = [...headersInfo, headerObj];

		for (const row of table.rows) {
			const rowObj = {};
			row.forEach((value, colIdx) => rowObj[alphabits[colIdx]] = value);
			dataObj.push(rowObj);
		}

		const period = this.periodDisplay.replace(/\s+/g, '-');
		const fileName = this.common.getCleanFileName(this.siteInfo.name)
			+ '-' + period
			+ '-' + this.common.getCleanFileName(chart.title);

		new AngularCsv(dataObj, fileName);
	}
}
